import { useRoute } from 'vue-router'
import useGet from '../../../core/composables/api/useGet'
import { nextTick, onMounted, reactive, ref, watch } from 'vue'
import type { Member } from '../../../core/models/member/member'
import usePost from '../../../core/composables/api/usePost'
import { ApiRequestStatus } from '../../../core/models/api/api-request-status'
import { useSnackStore } from '../../../stores/snack.store'
import { useEventBus } from '@vueuse/core'
import { EventKey } from '../../../core/events/event-key'
import { useQuestionAnalysisNew } from '@/core/composables/use-question-analysis-new'
import type { QuestionAnalysis, SkillAnalysis, TextAnalysis } from '@/core/models/surveys-analytics'

export const useEvaluationFeedbackTopicSlideAnalysis = (
  feedbackTopicId: string,
  allSurveyIds: string[],
  slideProperties: SlideProperties,
) => {
  const route = useRoute()
  const dialogId = route.params.id as string
  const snackStore = useSnackStore()
  const eventBus = useEventBus(EventKey.dialogSlideUpdated)

  const {
    data: analysisData,
    load,
    reload,
    status,
  } = useGet<DialogEvaluationSlideAnalysis>(
    `dialog/prepare/${dialogId}/evaluation-slide/${feedbackTopicId}/analysis`,
  )

  const { mutate, status: saveStatus } = usePost<string, EvaluationSlideInput>(
    `dialog/prepare/${dialogId}/evaluation-slide/${feedbackTopicId}`,
  )

  const hasChanged = ref(false)
  const isInitializing = ref(true)

  const inputData = reactive<EvaluationSlideInput>({
    slideId: feedbackTopicId,
    surveyIds: [],
    overestimateIds: [],
    potentialIds: [],
    strengthIds: [],
    underestimateIds: [],
    textIds: [],
  })

  const calculateSuggestions = (
    questions: ReadonlyArray<QuestionAnalysis>,
  ): Partial<EvaluationSlideInput> => {
    const { getTopStrength, getTopPotential, getTopOverestimate, getTopUnderestimate } =
      useQuestionAnalysisNew(questions, true)
    return {
      strengthIds: getTopStrength(1).map((q) => q.question.id),
      potentialIds: getTopPotential(1).map((q) => q.question.id),
      overestimateIds: getTopOverestimate(1).map((q) => q.question.id),
      underestimateIds: getTopUnderestimate(1).map((q) => q.question.id),
      textIds: [],
    }
  }

  const applyFormData = (source: Partial<EvaluationSlideInput>) => {
    if (source.surveyIds) inputData.surveyIds = [...source.surveyIds]
    if (source.strengthIds) inputData.strengthIds = [...source.strengthIds]
    if (source.potentialIds) inputData.potentialIds = [...source.potentialIds]
    if (source.overestimateIds) inputData.overestimateIds = [...source.overestimateIds]
    if (source.underestimateIds) inputData.underestimateIds = [...source.underestimateIds]
    if (source.textIds) inputData.textIds = [...source.textIds]
  }

  const hydrateForm = () => {
    isInitializing.value = true

    // Case 1: Existing saved data (Edit Mode)
    if (slideProperties.updatedAt) {
      applyFormData({
        surveyIds: [...slideProperties.surveyIds],
        strengthIds: [...slideProperties.strengthIds],
        potentialIds: [...slideProperties.potentialIds],
        overestimateIds: [...slideProperties.overestimateIds],
        underestimateIds: [...slideProperties.underestimateIds],
        textIds: [...slideProperties.textIds],
      })
    }
    // Case 2: New data derived from API Analysis (Create Mode / Auto-fill)
    else if (analysisData.value) {
      // Only apply suggestions if we aren't already dirty or if it's the very first load
      applyFormData({
        ...calculateSuggestions(analysisData.value.questions),
        surveyIds: allSurveyIds, // Default to all surveys for new
      })
    }
    // Case 3: Fallback / Initial State
    else {
      inputData.surveyIds = allSurveyIds
    }

    // Reset dirty state after hydration
    nextTick(() => {
      isInitializing.value = false
      hasChanged.value = false
    })
  }

  const updateSlide = async () => {
    await mutate(inputData)

    if (saveStatus.value === ApiRequestStatus.Success) {
      snackStore.showInfo('Daten gespeichert.')
      eventBus.emit({ dialogId, slideId: feedbackTopicId })
      hasChanged.value = false
    } else if (saveStatus.value === ApiRequestStatus.Error) {
      snackStore.showError('Daten nicht gespeichert.')
    }
  }

  onMounted(() => {
    const queryIds = slideProperties.updatedAt ? [...slideProperties.surveyIds] : allSurveyIds
    load({ query: { surveyIds: queryIds } })
  })

  watch(analysisData, () => {
    if (!hasChanged.value) {
      hydrateForm()
    }
  })

  // Deep watch for user changes
  watch(
    inputData,
    () => {
      if (isInitializing.value) return

      hasChanged.value = true
    },
    { deep: true },
  )

  return {
    data: analysisData,
    status,
    inputData,
    hasChanged,
    reload,
    updateSlide,
  }
}

export interface DialogEvaluationSlideAnalysis {
  readonly surveyIds: ReadonlyArray<string>
  readonly feedbackSenders: ReadonlyArray<Member>
  readonly skills: ReadonlyArray<SkillAnalysis>
  readonly questions: ReadonlyArray<QuestionAnalysis>
  readonly texts: ReadonlyArray<TextAnalysis>
}

export interface EvaluationSlideInput {
  slideId: string
  surveyIds: string[]
  strengthIds: string[]
  potentialIds: string[]
  underestimateIds: string[]
  overestimateIds: string[]
  textIds: string[]
}

interface SlideProperties {
  updatedAt: string | null
  surveyIds: ReadonlyArray<string>
  overestimateIds: ReadonlyArray<string>
  potentialIds: ReadonlyArray<string>
  strengthIds: ReadonlyArray<string>
  underestimateIds: ReadonlyArray<string>
  textIds: ReadonlyArray<string>
}
